import type {
  DocumentList,
  FolderId,
  PrincipalOptions,
  ProjectMemberView,
  ProjectView,
} from "@esi/shared";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type KnowledgeSettings, projectKeys } from "./queries.ts";
import { UploadForm } from "./UploadForm.tsx";

/**
 * The upload form in a modal dialog: focus is trapped inside and returns to the opening control
 * on close. Escape and an outside click cancel like the Cancel button: with files still on their
 * way the form asks first, and pending uploads are removed. The file list is refreshed whenever
 * the batch has nothing in flight; the dialog closes on its own once every file is uploaded.
 */
export function UploadDialog(props: {
  open: boolean;
  onClose: () => void;
  project: ProjectView;
  settings: KnowledgeSettings;
  /** Undefined while loading or when the list could not be loaded. */
  members: readonly ProjectMemberView[] | undefined;
  principals?: PrincipalOptions | undefined;
  /** The folder the upload starts in ("Upload here", the drilled root); null is the root. */
  folderId?: string | null;
}) {
  const { open, onClose, project, settings, members, principals } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const cancelRef = useRef<(() => void) | null>(null);
  const refresh = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: projectKeys.documents(project.id) }),
    [queryClient, project.id],
  );
  // Only files the user can read are listed, so a same-name warning reveals nothing new.
  // Subscribed to the project's list without fetching it here (the files view loads it).
  const documents = useQuery<DocumentList>({
    queryKey: projectKeys.documents(project.id),
    queryFn: skipToken,
  });
  const visibleTitles = documents.data?.documents.map((d) => d.title);
  const cancel = (e: Event) => {
    e.preventDefault();
    cancelRef.current?.();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : cancelRef.current?.())}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-xl"
        onEscapeKeyDown={cancel}
        onInteractOutside={cancel}
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{t("upload.title")}</DialogTitle>
          <DialogDescription className="sr-only">{project.name}</DialogDescription>
        </DialogHeader>
        {open ? (
          <UploadForm
            project={project}
            settings={settings}
            members={members}
            principals={principals}
            visibleTitles={visibleTitles}
            initialFolder={(props.folderId ?? null) as FolderId | null}
            onCancel={onClose}
            onSettled={refresh}
            onDone={onClose}
            cancelRef={cancelRef}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
